import { useFocusRing } from '@react-native-aria/focus'
import clsx from 'clsx'
import type { ReactNode } from 'react'
import { type PressableProps, View } from 'react-native'

import type { ICON } from '@/src/general/enums/icons'
import type { SlotsToClasses } from '@/src/types/slot-to-classes'

import { Icon } from '../../media/Icon'
import { Pressable } from '../../pressable'
import { Typography } from '../../text/Typography'
import { cardButtonVariants, type TCardButtonSlots } from './card-button-variants'

export type TCardButtonProps = {
  label: string
  description?: string
  startIcon?: ICON
  endIcon?: ICON
  selected?: boolean
  disabled?: boolean
  className?: string
  classNames?: SlotsToClasses<TCardButtonSlots> & { iconLeft?: string; iconRight?: string }
} & PressableProps

export const CardButton = ({
  label,
  description,
  startIcon,
  endIcon,
  selected = false,
  disabled = false,
  onPress,
  testID,
  className,
  classNames,
  ...otherProps
}: TCardButtonProps): ReactNode => {
  const { isFocusVisible, focusProps } = useFocusRing()
  const slots = cardButtonVariants({ selected, disabled })

  return (
    <Pressable
      className={slots.base({ class: clsx(className, classNames?.base) })}
      dataSet={{ 'focus-visible': isFocusVisible }}
      testID={testID}
      {...focusProps}
      {...otherProps}
      disabled={disabled}
      onPress={disabled ? undefined : onPress}
    >
      {startIcon && (
        <Icon
          name={startIcon}
          size="md"
          className={slots.icons({ class: clsx(classNames?.icons, classNames?.iconLeft) })}
          testID={testID && `${testID}-start-icon`}
        />
      )}
      <View className={slots.content({ class: classNames?.content })}>
        <Typography size="md" className={slots.label({ class: classNames?.label })}>
          {label}
        </Typography>
        {description && (
          <Typography size="sm" className={slots.description({ class: classNames?.description })}>
            {description}
          </Typography>
        )}
      </View>
      {endIcon && (
        <Icon
          name={endIcon}
          size="md"
          className={slots.icons({ class: clsx(classNames?.icons, classNames?.iconRight) })}
          testID={testID && `${testID}-end-icon`}
        />
      )}
    </Pressable>
  )
}
